import { useMemo, type FC } from "react";
import { NavLink } from "react-router-dom";
import { useCertificatePolicies } from "../stores/certificate_policy_store";
import { useDeviceGroups } from "../stores/device_group_store";
import { useCertificates } from "../stores/certificate_store";
import { useCustomerId } from "../stores/customer_id_store";
import { UnassignedPlaceholder } from "../components/unassigned_placeholder";
import { IssuanceGraphSection } from "../components/issuance_graph_section";
import { TimeAgo } from "../components/time_ago";
import { daysUntil } from "../lib/date_utils";
import { modalManager } from "../lib/modal_store";

import type { Certificate } from "../../../shared/models/certificate";

export const Dashboard: FC = () => {
  const customerId = useCustomerId();
  const policies = useCertificatePolicies();
  const groups = useDeviceGroups();
  const certificates = useCertificates();

  const groupMap = useMemo(() => {
    const m = new Map<string, string>();
    groups.forEach((g) => m.set(g.id, g.name));
    return m;
  }, [groups]);

  const groupById = useMemo(() => {
    const m = new Map<string, (typeof groups)[number]>();
    groups.forEach((g) => m.set(g.id, g));
    return m;
  }, [groups]);

  const policyMap = useMemo(() => {
    const m = new Map<string, string>();
    policies.forEach((p) => m.set(p.id, p.name));
    return m;
  }, [policies]);

  const policyById = useMemo(() => {
    const m = new Map<string, (typeof policies)[number]>();
    policies.forEach((p) => m.set(p.id, p));
    return m;
  }, [policies]);

  const recentCerts = useMemo(
    () =>
      [...certificates]
        .sort(
          (a, b) =>
            new Date(b.validFrom).getTime() - new Date(a.validFrom).getTime()
        )
        .slice(0, 5),
    [certificates]
  );

  const revokedCerts = useMemo(
    () =>
      certificates
        .filter((c) => c.status === "revoked")
        .sort(
          (a, b) =>
            new Date(b.revokedAt ?? b.validFrom).getTime() -
            new Date(a.revokedAt ?? a.validFrom).getTime()
        )
        .slice(0, 5),
    [certificates]
  );

  const expiringCerts = useMemo(
    () =>
      certificates
        .filter((c) => c.status === "expiring")
        .sort(
          (a, b) =>
            new Date(a.validTo).getTime() - new Date(b.validTo).getTime()
        )
        .slice(0, 5),
    [certificates]
  );

  const recentlyExpired = useMemo(
    () =>
      certificates
        .filter((c) => c.status === "expired")
        .sort(
          (a, b) =>
            new Date(b.validTo).getTime() - new Date(a.validTo).getTime()
        )
        .slice(0, 5),
    [certificates]
  );

  const openGroupDetail = (groupId: string) => {
    const group = groupById.get(groupId);
    if (!group) return;
    modalManager.open("groupDetail", {
      group,
      policy: policyById.get(group.certificatePolicyId),
      onPolicyClick: () => {
        const p = policyById.get(group.certificatePolicyId);
        if (p) modalManager.open("policyDetail", { policy: p });
      },
    });
  };

  const openCertDetail = (cert: Certificate) => {
    modalManager.open("certificateDetail", {
      cert,
      groupName: groupMap.get(cert.deviceGroupId),
      policyName: policyMap.get(cert.certificatePolicyId),
      onGroupClick: () => openGroupDetail(cert.deviceGroupId),
      onPolicyClick: () => {
        const p = policyById.get(cert.certificatePolicyId);
        if (p) modalManager.open("policyDetail", { policy: p });
      },
    });
  };

  if (!customerId) {
    return <UnassignedPlaceholder />;
  }

  return (
    <div className="vmx-page">
      <IssuanceGraphSection />

      {/* Expiring Soon + Recently Issued Certificates */}
      {certificates.length > 0 && (
        <div className="vmx-dashboard-grid">
          <CertSection
            order={1}
            icon="🆕"
            title="Certificates Recently Issued"
            link="/certificates"
            thirdCol="Created"
            emptyText="No recently issued certificates"
            certs={recentCerts}
            renderThirdCol={(c) => <TimeAgo iso={c.validFrom} />}
            groupMap={groupMap}
            onRowClick={openCertDetail}
            onGroupClick={openGroupDetail}
          />
          <CertSection
            order={2}
            icon="⚠"
            title="Certificates Expiring Soon"
            link="/certificates?tab=expiring"
            thirdCol="Remaining"
            emptyText="No certificates expiring soon"
            certs={expiringCerts}
            renderThirdCol={(c) => {
              const days = daysUntil(c.validTo);
              return (
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: "0.8125rem",
                    color:
                      days <= 7 ? "var(--vmx-danger)" : "var(--vmx-warning)",
                  }}
                >
                  {days}d
                </span>
              );
            }}
            groupMap={groupMap}
            onRowClick={openCertDetail}
            onGroupClick={openGroupDetail}
          />
          <CertSection
            order={3}
            icon="⛔"
            title="Certificates Recently Revoked"
            link="/certificates?tab=revoked"
            thirdCol="Revoked"
            emptyText="No revoked certificates"
            certs={revokedCerts}
            renderThirdCol={(c) => <TimeAgo iso={c.revokedAt} />}
            groupMap={groupMap}
            onRowClick={openCertDetail}
            onGroupClick={openGroupDetail}
          />
          <CertSection
            order={4}
            icon="⏰"
            title="Certificates Recently Expired"
            link="/certificates?tab=expired"
            thirdCol="Expired"
            emptyText="No expired certificates"
            certs={recentlyExpired}
            renderThirdCol={(c) => <TimeAgo iso={c.validTo} />}
            groupMap={groupMap}
            onRowClick={openCertDetail}
            onGroupClick={openGroupDetail}
          />
        </div>
      )}

      {/* Keep the dashboard focused on operations; place revocation visibility next to quick actions. */}
      <div className="vmx-card">
        <h3
          style={{
            fontSize: "1rem",
            fontWeight: 700,
            color: "var(--vmx-gray-900)",
            marginBottom: 20,
          }}
        >
          Quick Actions
        </h3>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <QuickAction
            to="/device-groups?action=create"
            icon="＋"
            title="Create Device Group"
            description="Set up a new device group with a policy"
            color="var(--vmx-accent)"
          />
          <QuickAction
            to="/certificate-policies?action=create"
            icon="＋"
            title="Create Certificate Policy"
            description="Define authentication rules for devices"
            color="var(--vmx-info)"
          />
          <QuickAction
            to="/device-groups"
            icon="⇆"
            title="Manage Associations"
            description="Update device group policy assignments"
            color="var(--vmx-success)"
          />
        </div>
      </div>
    </div>
  );
};

/* ── Sub-components ── */

const CertSection: FC<{
  order: number;
  icon: string;
  title: string;
  link: string;
  thirdCol: string;
  emptyText: string;
  certs: Certificate[];
  renderThirdCol: (c: Certificate) => React.ReactNode;
  groupMap: Map<string, string>;
  onRowClick: (c: Certificate) => void;
  onGroupClick: (groupId: string) => void;
}> = ({
  order,
  icon,
  title,
  link,
  thirdCol,
  emptyText,
  certs,
  renderThirdCol,
  groupMap,
  onRowClick,
  onGroupClick,
}) => (
  <div style={{ order }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: 16,
      }}
    >
      <h3
        style={{
          fontSize: "1rem",
          fontWeight: 700,
          color: "var(--vmx-gray-900)",
        }}
      >
        {icon} {title}
      </h3>
      <NavLink to={link}>
        <button className="vmx-btn vmx-btn-ghost vmx-btn-sm">View all →</button>
      </NavLink>
    </div>
    {certs.length === 0 ? (
      <div
        className="vmx-card"
        style={{
          textAlign: "center",
          padding: 32,
          color: "var(--vmx-gray-400)",
          fontSize: "0.875rem",
        }}
      >
        {emptyText}
      </div>
    ) : (
      <div className="vmx-table-wrapper">
        <table className="vmx-table">
          <thead>
            <tr>
              <th>Device ID</th>
              <th>Group</th>
              <th>{thirdCol}</th>
            </tr>
          </thead>
          <tbody>
            {certs.map((c) => (
              <tr
                key={c.deviceId}
                onClick={() => onRowClick(c)}
                style={{ cursor: "pointer" }}
              >
                <td
                  style={{
                    fontFamily: "var(--vmx-font-mono)",
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    color: "var(--vmx-gray-800)",
                  }}
                >
                  {c.deviceId}
                </td>
                <td style={{ fontSize: "0.8125rem" }}>
                  {groupMap.has(c.deviceGroupId) ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onGroupClick(c.deviceGroupId);
                      }}
                      style={{
                        color: "var(--vmx-accent-dark)",
                        fontWeight: 600,
                        background: "none",
                        border: "none",
                        padding: 0,
                        cursor: "pointer",
                      }}
                    >
                      {groupMap.get(c.deviceGroupId)}
                    </button>
                  ) : (
                    <span
                      style={{
                        fontFamily: "var(--vmx-font-mono)",
                        fontSize: "0.75rem",
                        color: "var(--vmx-gray-500)",
                      }}
                    >
                      {c.deviceGroupId}
                    </span>
                  )}
                </td>
                <td
                  style={{
                    color: "var(--vmx-gray-500)",
                    fontSize: "0.8125rem",
                  }}
                >
                  {renderThirdCol(c)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

const QuickAction: FC<{
  to: string;
  icon: string;
  title: string;
  description: string;
  color: string;
}> = ({ to, icon, title, description, color }) => (
  <NavLink to={to} style={{ textDecoration: "none" }}>
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        padding: "14px 16px",
        borderRadius: "var(--vmx-radius)",
        border: "var(--vmx-border)",
        transition: "all var(--vmx-transition)",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.borderColor = color;
        e.currentTarget.style.boxShadow = `0 0 0 1px ${color}20`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = "var(--vmx-gray-200)";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      <span
        style={{
          width: 40,
          height: 40,
          borderRadius: "var(--vmx-radius)",
          background: `${color}15`,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "1.25rem",
          fontWeight: 700,
          flexShrink: 0,
        }}
      >
        {icon}
      </span>
      <div>
        <div
          style={{
            fontSize: "0.875rem",
            fontWeight: 600,
            color: "var(--vmx-gray-800)",
          }}
        >
          {title}
        </div>
        <div
          style={{
            fontSize: "0.75rem",
            color: "var(--vmx-gray-400)",
            marginTop: 2,
          }}
        >
          {description}
        </div>
      </div>
    </div>
  </NavLink>
);
